import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Building2, ExternalLink, Loader2, Briefcase, ShoppingBag } from "lucide-react";

const CorporateStorePublic = () => {
  const { corporateId } = useParams();
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, prodRes] = await Promise.all([
          axios.get(`${API}/corporate/${corporateId}`),
          axios.get(`${API}/products?artist_id=${corporateId}`)
        ]);
        setProfile(pRes.data);
        setProducts(prodRes.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Store not found");
      } finally { setLoading(false); }
    })();
  }, [corporateId]);

  if (loading) return <div className="min-h-screen bg-[#000000] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#EC008C]" /></div>;
  if (error) return <div className="min-h-screen bg-[#000000] flex items-center justify-center text-[#8A92A0]">{error}</div>;

  return (
    <div className="min-h-screen bg-[#000000]" data-testid="corporate-store-public">
      {/* Hero */}
      <div className="relative bg-gradient-to-b from-[#EC008C]/10 to-transparent border-b border-[#1A2030]">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Link to="/home" className="inline-flex items-center gap-2 text-sm text-[#8A92A0] hover:text-white mb-6">
            <img src="/logo.png" alt="" className="w-8 h-8 object-contain" style={{ mixBlendMode: "lighten" }} />
            <span>StardomFans.com</span>
          </Link>
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-[#EC008C]/15 border border-[#EC008C]/40 flex items-center justify-center flex-shrink-0">
              {profile.logo_url ? <img src={profile.logo_url} alt={profile.company_name} className="w-full h-full object-cover rounded-2xl" /> : <Building2 className="w-10 h-10 text-[#EC008C]" />}
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wider text-[#EC008C] font-bold mb-1">Sponsor Store</p>
              <h1 className="font-['Unbounded'] text-3xl sm:text-4xl font-bold text-white mb-2">{profile.company_name}</h1>
              {profile.description && <p className="text-[#8A92A0] max-w-2xl">{profile.description}</p>}
              {profile.business_website && (
                <a href={profile.business_website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-[#EC008C] hover:text-[#FF3399] mt-2">
                  {profile.business_website.replace(/^https?:\/\//, "")} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="max-w-6xl mx-auto px-6 py-10">
        <h2 className="font-['Unbounded'] text-xl font-semibold text-white mb-5 flex items-center gap-2">
          <Briefcase className="w-5 h-5 text-[#EC008C]" /> Shop
          <span className="text-sm text-[#8A92A0] font-normal">({products.length})</span>
        </h2>
        {products.length === 0 ? (
          <div className="card p-12 text-center text-[#8A92A0]" data-testid="empty-store-state">
            <ShoppingBag className="w-10 h-10 mx-auto mb-3 opacity-50" />
            This store hasn&apos;t listed products yet.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map(p => (
              <Link key={p.product_id} to={`/products/${p.product_id}`} className="card p-5 group hover:border-[#EC008C]/40 transition-colors" data-testid={`product-card-${p.product_id}`}>
                <div className="aspect-square rounded-lg bg-[#1A2030] mb-4 overflow-hidden flex items-center justify-center">
                  {p.images?.[0] ? <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <Briefcase className="w-10 h-10 text-[#8A92A0]" />}
                </div>
                <h3 className="font-semibold text-white mb-1 line-clamp-1">{p.name}</h3>
                <p className="text-xs text-[#8A92A0] line-clamp-2 mb-2">{p.description}</p>
                <p className="text-[#EC008C] font-bold">${parseFloat(p.price).toFixed(2)}</p>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CorporateStorePublic;
